import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Snackbar,
  TextField,
  Typography,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useRef, useMemo, useState } from "react";

async function fetchSources() {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + "/kb/sources"
  );
  return response.data;
}

export default function KnowledgeBase() {
  const queryClient = useQueryClient();

  const [ingestText, setIngestText] = useState("");
  const [ingestSource, setIngestSource] = useState("");
  const [ingestEmployer, setIngestEmployer] = useState(null);
  const [ingestCardType, setIngestCardType] = useState("");
  const [ingestAudience, setIngestAudience] = useState("ALL");
  const [fileSource, setFileSource] = useState("");
  const [fileEmployer, setFileEmployer] = useState(null);
  const [fileCardType, setFileCardType] = useState("");
  const [fileAudience, setFileAudience] = useState("ALL");
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const [queryText, setQueryText] = useState("");
  const [queryEmployer, setQueryEmployer] = useState(null);
  const [queryCardType, setQueryCardType] = useState("");
  const [queryAudience, setQueryAudience] = useState("ALL");
  const [queryResults, setQueryResults] = useState(null);

  const [feedback, setFeedback] = useState({
    open: false,
    type: "success",
    message: "",
  });

  const showFeedback = (type, message) =>
    setFeedback({ open: true, type, message });

  // ── Fetch sources ────────────────────────────────────────────────────────
  const { data, isLoading } = useQuery(["kb-sources"], fetchSources, {
    onError: (e) =>
      showFeedback("error", "Failed to load sources: " + (e?.message ?? e)),
  });
  const sources = data?.sources ?? [];
  const rows = sources.map((s, i) => ({
    id: i,
    source: typeof s === "object" ? s.source : s,
    employerId: typeof s === "object" ? (s.employerId ?? "ALL") : "ALL",
    cardType: typeof s === "object" ? (s.cardType ?? "ALL") : "ALL",
    audience: typeof s === "object" ? (s.audience ?? "ALL") : "ALL",
  }));

  // ── Fetch employers (for autocomplete) ──────────────────────────────────
  const { data: employersData, isLoading: isLoadingEmployers } = useQuery(
    ["employers"],
    () =>
      HttpService.getAxiosClient().get(
        window.API_URL + `/employer/all?excludeRelations=true`
      ),
    { staleTime: 5 * 60 * 1000 }
  );

  // ── Delete source ────────────────────────────────────────────────────────
  const deleteMutation = useMutation(
    (source) =>
      HttpService.getAxiosClient().delete(
        window.API_URL + `/kb/source/${encodeURIComponent(source)}`
      ),
    {
      onSuccess: (_, source) => {
        queryClient.invalidateQueries(["kb-sources"]);
        showFeedback("success", `Source "${source}" deleted`);
      },
      onError: (e) =>
        showFeedback("error", "Delete failed: " + (e?.message ?? e)),
    }
  );

  // ── Ingest text ──────────────────────────────────────────────────────────
  const ingestMutation = useMutation(
    () =>
      HttpService.getAxiosClient().post(window.API_URL + "/kb/ingest", {
        text: ingestText,
        source: ingestSource || "unknown",
        employerId: ingestEmployer?.idEmployer || "ALL",
        cardType: ingestCardType || "ALL",
        audience: ingestAudience || "ALL",
      }),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(["kb-sources"]);
        showFeedback(
          "success",
          `Ingested ${res.data?.chunks ?? "?"} chunks from source "${res.data?.source}"`
        );
        setIngestText("");
        setIngestSource("");
        setIngestEmployer(null);
        setIngestCardType("");
        setIngestAudience("ALL");
      },
      onError: (e) =>
        showFeedback("error", "Ingest failed: " + (e?.message ?? e)),
    }
  );

  // ── Search / test query ──────────────────────────────────────────────────
  const searchMutation = useMutation(
    () =>
      HttpService.getAxiosClient().post(window.API_URL + "/kb/search", {
        query: queryText,
        employerId: queryEmployer?.idEmployer || "ALL",
        cardType: queryCardType || "ALL",
        audience: queryAudience || "ALL",
      }),
    {
      onSuccess: (res) => setQueryResults(res.data?.results ?? []),
      onError: (e) =>
        showFeedback("error", "Search failed: " + (e?.message ?? e)),
    }
  );

  // ── Upload file ──────────────────────────────────────────────────────────
  const uploadMutation = useMutation(
    () =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          // reader.result = "data:<mime>;base64,<data>"
          const base64 = reader.result.split(",")[1];
          HttpService.getAxiosClient()
            .post(window.API_URL + "/kb/ingest/file", {
              fileBase64: base64,
              fileName: selectedFile.name,
              source: fileSource || selectedFile.name,
              employerId: fileEmployer?.idEmployer || "ALL",
              cardType: fileCardType || "ALL",
              audience: fileAudience || "ALL",
            })
            .then(resolve)
            .catch(reject);
        };
        reader.onerror = reject;
        reader.readAsDataURL(selectedFile);
      }),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(["kb-sources"]);
        showFeedback(
          "success",
          `Uploaded "${res.data?.file}" — ${res.data?.chunks ?? "?"} chunks from source "${res.data?.source}"`
        );
        setSelectedFile(null);
        setFileSource("");
        setFileEmployer(null);
        setFileCardType("");
        setFileAudience("ALL");
        if (fileInputRef.current) fileInputRef.current.value = "";
      },
      onError: (e) =>
        showFeedback("error", "Upload failed: " + (e?.message ?? e)),
    }
  );

  // ── Columns ──────────────────────────────────────────────────────────────
  const employerNameById = useMemo(() => {
    const map = {};
    for (const emp of employersData?.data ?? []) {
      map[emp.idEmployer] = emp.name || emp.idEmployer;
    }
    return map;
  }, [employersData]);

  const columns = [
    { field: "source", headerName: "Source", flex: 1 },
    {
      field: "employerId",
      headerName: "Employer",
      width: 150,
      renderCell: (params) =>
        params.value && params.value !== "ALL" ? (
          <Chip
            label={employerNameById[params.value] ?? params.value}
            size="small"
            variant="outlined"
            color="primary"
          />
        ) : (
          <Chip label="ALL" size="small" variant="outlined" />
        ),
    },
    {
      field: "cardType",
      headerName: "Card Type",
      width: 130,
      renderCell: (params) =>
        params.value && params.value !== "ALL" ? (
          <Chip
            label={params.value}
            size="small"
            variant="outlined"
            color="secondary"
          />
        ) : (
          <Chip label="ALL" size="small" variant="outlined" />
        ),
    },
    {
      field: "audience",
      headerName: "Audience",
      width: 140,
      renderCell: (params) => <Chip label={params.value || "ALL"} size="small" variant="outlined" />,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 130,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => deleteMutation.mutate(params.row.source)}
        >
          Delete
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Knowledge Base Management
      </Typography>

      {/* ── Sources table ─────────────────────────────────────────────── */}
      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
        Sources
      </Typography>
      {isLoading ? (
        <CircularProgress size={24} />
      ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          autoHeight
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          sx={{ mb: 4 }}
        />
      )}

      {/* ── Ingest text ───────────────────────────────────────────────── */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        Ingest Text
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <TextField
            label="Source label"
            value={ingestSource}
            onChange={(e) => setIngestSource(e.target.value)}
            size="small"
            sx={{ width: 300, mr: 2 }}
          />
          <Autocomplete
            options={employersData?.data || []}
            getOptionLabel={(option) =>
              `${option.name || ""} [${option.idEmployer || ""}]`
            }
            value={ingestEmployer}
            onChange={(_, newValue) => setIngestEmployer(newValue)}
            loading={isLoadingEmployers}
            isOptionEqualToValue={(option, value) =>
              option.idEmployer === value?.idEmployer
            }
            sx={{ width: 260, mr: 2, display: "inline-flex" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Employer"
                size="small"
                placeholder="ALL"
              />
            )}
          />
          <TextField
            label="Card Type"
            value={ingestCardType}
            onChange={(e) => setIngestCardType(e.target.value)}
            size="small"
            placeholder="ALL"
            sx={{ width: 160 }}
          />
          <TextField
            select
            label="Audience"
            value={ingestAudience}
            onChange={(e) => setIngestAudience(e.target.value)}
            size="small"
            sx={{ width: 160, ml: 2 }}
          >
            <MenuItem value="ALL">ALL</MenuItem>
            <MenuItem value="CUSTOMER">CUSTOMER</MenuItem>
            <MenuItem value="ONBOARDING">ONBOARDING</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Text content"
            value={ingestText}
            onChange={(e) => setIngestText(e.target.value)}
            multiline
            minRows={4}
            fullWidth
          />
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            onClick={() => ingestMutation.mutate()}
            disabled={!ingestText.trim() || ingestMutation.isLoading}
          >
            {ingestMutation.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Ingest"
            )}
          </Button>
        </Grid>
      </Grid>

      {/* ── Upload file ───────────────────────────────────────────────── */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        Upload File (PDF / TXT)
      </Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          <TextField
            label="Source label"
            value={fileSource}
            onChange={(e) => setFileSource(e.target.value)}
            size="small"
            sx={{ width: 300, mr: 2 }}
          />
          <Autocomplete
            options={employersData?.data || []}
            getOptionLabel={(option) =>
              `${option.name || ""} [${option.idEmployer || ""}]`
            }
            value={fileEmployer}
            onChange={(_, newValue) => setFileEmployer(newValue)}
            loading={isLoadingEmployers}
            isOptionEqualToValue={(option, value) =>
              option.idEmployer === value?.idEmployer
            }
            sx={{ width: 260, mr: 2, display: "inline-flex" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Employer"
                size="small"
                placeholder="ALL"
              />
            )}
          />
          <TextField
            label="Card Type"
            value={fileCardType}
            onChange={(e) => setFileCardType(e.target.value)}
            size="small"
            placeholder="ALL"
            sx={{ width: 160 }}
          />
          <TextField
            select
            label="Audience"
            value={fileAudience}
            onChange={(e) => setFileAudience(e.target.value)}
            size="small"
            sx={{ width: 160, ml: 2 }}
          >
            <MenuItem value="ALL">ALL</MenuItem>
            <MenuItem value="CUSTOMER">CUSTOMER</MenuItem>
            <MenuItem value="ONBOARDING">ONBOARDING</MenuItem>
          </TextField>
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
          >
            Choose File
            <input
              type="file"
              accept=".pdf,.txt"
              hidden
              ref={fileInputRef}
              onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
            />
          </Button>
          {selectedFile && (
            <Typography variant="body2" sx={{ ml: 1, display: "inline" }}>
              {selectedFile.name}
            </Typography>
          )}
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            onClick={() => uploadMutation.mutate()}
            disabled={!selectedFile || uploadMutation.isLoading}
          >
            {uploadMutation.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Upload & Ingest"
            )}
          </Button>
        </Grid>
      </Grid>

      {/* ── Test Query ───────────────────────────────────────────────── */}
      <Divider sx={{ my: 4 }} />
      <Typography variant="h6" sx={{ mb: 1 }}>
        Test Query
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <TextField
            label="Query"
            value={queryText}
            onChange={(e) => {
              setQueryText(e.target.value);
              setQueryResults(null);
            }}
            multiline
            minRows={2}
            fullWidth
            placeholder="Type a question to search the knowledge base..."
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            options={employersData?.data || []}
            getOptionLabel={(option) =>
              `${option.name || ""} [${option.idEmployer || ""}]`
            }
            value={queryEmployer}
            onChange={(_, newValue) => setQueryEmployer(newValue)}
            loading={isLoadingEmployers}
            isOptionEqualToValue={(option, value) =>
              option.idEmployer === value?.idEmployer
            }
            sx={{ width: 260, mr: 2, display: "inline-flex" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Employer"
                size="small"
                placeholder="ALL"
              />
            )}
          />
          <TextField
            label="Card Type"
            value={queryCardType}
            onChange={(e) => setQueryCardType(e.target.value)}
            size="small"
            placeholder="ALL"
            sx={{ width: 160 }}
          />
          <TextField
            select
            label="Audience"
            value={queryAudience}
            onChange={(e) => setQueryAudience(e.target.value)}
            size="small"
            sx={{ width: 160, ml: 2 }}
          >
            <MenuItem value="ALL">ALL</MenuItem>
            <MenuItem value="CUSTOMER">CUSTOMER</MenuItem>
            <MenuItem value="ONBOARDING">ONBOARDING</MenuItem>
          </TextField>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            onClick={() => searchMutation.mutate()}
            disabled={!queryText.trim() || searchMutation.isLoading}
          >
            {searchMutation.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Search"
            )}
          </Button>
        </Grid>
      </Grid>

      {queryResults !== null && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {queryResults.length === 0
              ? "No matching chunks found."
              : `${queryResults.length} chunk(s) found:`}
          </Typography>
          {queryResults.map((match, i) => {
            const score = typeof match === "object" ? match.score : null;
            const source = typeof match === "object" ? match.source : null;
            const text = typeof match === "object" ? match.text : match;
            const chunkEmployer = match?.employerId;
            const chunkCardType = match?.cardType;
            const scoreColor =
              score >= 0.85 ? "success" : score >= 0.7 ? "warning" : "default";
            return (
              <Paper key={i} variant="outlined" sx={{ p: 2, mb: 1 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Result {i + 1}
                  </Typography>
                  {score !== null && (
                    <Chip
                      label={`score: ${score}`}
                      size="small"
                      color={scoreColor}
                      variant="outlined"
                    />
                  )}
                  {source && (
                    <Chip label={source} size="small" variant="outlined" />
                  )}
                  {chunkEmployer && chunkEmployer !== "ALL" && (
                    <Chip
                      label={`employer: ${employerNameById[chunkEmployer] ?? chunkEmployer}`}
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  )}
                  {chunkCardType && chunkCardType !== "ALL" && (
                    <Chip
                      label={`card: ${chunkCardType}`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  )}
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-wrap",
                    fontFamily: "monospace",
                    fontSize: 13,
                  }}
                >
                  {text}
                </Typography>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* ── Feedback snackbar ─────────────────────────────────────────── */}
      <Snackbar
        open={feedback.open}
        autoHideDuration={4000}
        onClose={() => setFeedback((f) => ({ ...f, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setFeedback((f) => ({ ...f, open: false }))}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
